// Constants
import { CUSTOM_FIELD_GROUPING_OPTION_PREFIX } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CUSTOM_FIELD_COLUMN_PREFIX } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { CUSTOM_FIELD_FILTER_PREFIX } from "../constants/FilterConstants";
// Models
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { getLocaleDateFormatPattern } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { Temporal } from "temporal-polyfill-lite";
import { CustomFieldChoice } from "../client/planner/customFields/CustomFieldChoice";
import { CustomFieldDefinition } from "../client/planner/customFields/CustomFieldDefinition";
import { CustomFieldRollup, CustomFieldValueType } from "../service/planner/enumerations/IPlannerEnumerations";
/**
 * Creates a temporary client-side custom field definition for use in the "add field" flow.
 * The returned object has no server state — it carries a caller-supplied temp id and
 * placeholder values for fields that are only populated after server creation (archivalInfo,
 * createdBy, etc.).
 *
 * @param fieldId     Caller-generated temporary id (e.g. from LocalIdGenerator.generate())
 * @param planId      Id of the plan the field will belong to
 */ export function createTempCustomFieldDefinition(fieldId, planId) {
    const now = Temporal.Now.instant();
    const defaultIdentitySet = {
        user: {
            id: "",
            tenantId: ""
        },
        application: {
            id: "",
            tenantId: ""
        }
    };
    return new CustomFieldDefinition({
        id: fieldId,
        version: "1",
        planId,
        displayName: "",
        valueType: CustomFieldValueType.Text,
        rollupType: CustomFieldRollup.NoRollup,
        choiceOptions: [],
        archivalInfo: {
            isArchived: false,
            justification: null,
            modifiedBy: null,
            modifiedDate: null
        },
        createdBy: defaultIdentitySet,
        createdDateTime: now,
        lastModifiedBy: defaultIdentitySet,
        lastModifiedDateTime: now,
        deletedBy: null,
        deletedDateTime: null
    });
}
/**
 * Creates a temporary client-side custom field choice for use in the "add/edit field" flow.
 * The returned object has no server state — it carries a caller-supplied temp id and
 * extensions until the parent field is persisted.
 *
 * @param choiceId      Caller-generated temporary id (e.g. from LocalIdGenerator.generate())
 * @param displayName   Initial display name for the choice
 * @param orderHint     Order hint that determines display position among sibling choices
 */ export function createTempCustomFieldChoice(choiceId, displayName, orderHint) {
    return new CustomFieldChoice({
        id: choiceId,
        version: "1",
        displayName,
        orderHint,
        color: null,
        extensions: {
            orderHint
        }
    });
}
/**
 * Returns a sorted copy of the choices array ordered ascending by orderHint.
 * This is the canonical display order: smaller orderHint → earlier in the list.
 */ export function getSortedCustomFieldChoices(choices) {
    return [
        ...choices
    ].sort((a, b)=>compareOrderHints(a.getOrderHint(), b.getOrderHint()));
}
/**
 * Groups choice ids by their normalized (trimmed, lower-cased) display name, preserving array order
 * within each group. Blank names are ignored — they are placeholders mid-edit, not collisions.
 */ function groupChoiceIdsByNormalizedName(choices) {
    const idsByNormalizedName = new Map();
    for (const choice of choices){
        // Locale-invariant lowercasing keeps duplicate detection consistent across user locales
        // (e.g. avoids the Turkish dotted/dotless-I divergence of toLocaleLowerCase).
        const normalizedName = choice.displayName.trim().toLowerCase();
        if (normalizedName === "") {
            continue;
        }
        const existingIds = idsByNormalizedName.get(normalizedName);
        if (existingIds != null) {
            existingIds.push(choice.id);
        } else {
            idsByNormalizedName.set(normalizedName, [
                choice.id
            ]);
        }
    }
    return idsByNormalizedName;
}
/**
 * Returns the set of choice ids whose display name collides with another choice's name.
 * Names are compared case-insensitively after trimming; blank names are ignored.
 *
 * Used to block submission in the add/edit field experience whenever any duplicate exists,
 * matching the uniqueness constraint enforced by Premium Plans.
 */ export function getDuplicateCustomFieldChoiceIds(choices) {
    const duplicateIds = new Set();
    for (const ids of groupChoiceIdsByNormalizedName(choices).values()){
        if (ids.length > 1) {
            for (const id of ids){
                duplicateIds.add(id);
            }
        }
    }
    return duplicateIds;
}
/**
 * Returns whether the definition's display name duplicates another definition or a non-custom field
 * name, compared case-insensitively after trimming. The definition itself is excluded by id.
 */ export function hasDuplicateCustomFieldDefinitionName(definition, existingDefinitions, existingNonCustomFieldNames = []) {
    const normalizedName = definition.displayName.trim().toLowerCase();
    return normalizedName !== "" && (existingNonCustomFieldNames.some((fieldName)=>fieldName.trim().toLowerCase() === normalizedName) || existingDefinitions.some((existingDefinition)=>existingDefinition.id !== definition.id && existingDefinition.displayName.trim().toLowerCase() === normalizedName));
}
/**
 * Returns the choice ids that should surface the duplicate-name error inline: exactly one per
 * colliding group, so the two halves of a pair never light up at once. Within a group the
 * `preferredId` (the choice the user is actively editing) is flagged when present; otherwise the
 * last choice in array order is flagged.
 *
 * Unlike {@link getDuplicateCustomFieldChoiceIds}, this always flags at least one id per collision
 * regardless of edit history — so a submit blocked by a duplicate (e.g. one produced by adding a
 * choice rather than typing) always has a visible explanation.
 */ export function getDuplicateChoiceIdsToFlag(choices, preferredId) {
    const flaggedIds = new Set();
    for (const ids of groupChoiceIdsByNormalizedName(choices).values()){
        if (ids.length > 1) {
            const flaggedId = preferredId != null && ids.includes(preferredId) ? preferredId : ids[ids.length - 1];
            flaggedIds.add(flaggedId);
        }
    }
    return flaggedIds;
}
/**
 * Returns true if the given value type supports grouping in the board view.
 * Currently SingleChoiceText, Date, and Flag fields are groupable.
 */ export function isGroupableCustomFieldDefinition(valueType) {
    return valueType === CustomFieldValueType.SingleChoiceText || valueType === CustomFieldValueType.Date || valueType === CustomFieldValueType.Flag;
}
/**
 * Returns a new {@link ICustomFieldDefinition} that is identical to {@code definition} except
 * with {@code newValueType} as the valueType.  Also resets dependent fields:
 *
 * - rollup → NoRollup when switching away from Number
 * - choiceOptions → empty when switching away from SingleChoiceText
 *
 * Use this helper when the user changes the field type in the add/edit popover because
 * `valueType` is intentionally absent from {@link MutableCustomFieldDefinitionProperties}
 * (the server does not support mutating it on an existing persisted field).
 */ export function cloneCustomFieldDefinitionWithValueType(definition, newValueType) {
    return new CustomFieldDefinition({
        id: definition.id,
        version: definition.version,
        planId: definition.planId,
        displayName: definition.displayName,
        valueType: newValueType,
        rollupType: newValueType === CustomFieldValueType.Number ? definition.rollupType : CustomFieldRollup.NoRollup,
        choiceOptions: newValueType === CustomFieldValueType.SingleChoiceText ? definition.choiceOptions : [],
        archivalInfo: definition.archivalInfo,
        createdBy: definition.createdBy,
        createdDateTime: definition.createdDateTime,
        lastModifiedBy: definition.lastModifiedBy,
        lastModifiedDateTime: definition.lastModifiedDateTime,
        deletedBy: definition.deletedBy,
        deletedDateTime: definition.deletedDateTime
    });
}
/**
 * Convert a custom field definition ID to a column ID
 * @param definitionId The custom field definition ID
 * @returns The prefixed column ID (e.g. "cf_<definitionId>")
 */ export function toCustomFieldColumnId(definitionId) {
    return `${CUSTOM_FIELD_COLUMN_PREFIX}${definitionId}`;
}
/**
 * Check if a column ID represents a custom field column
 * @param columnId The column ID to check
 * @returns True if the column ID has the custom field prefix
 */ export function isCustomFieldColumn(columnId) {
    return columnId.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);
}
/**
 * Convert a custom field definition ID to a filter field ID
 * @param definitionId The custom field definition ID
 * @returns The prefixed filter field ID (e.g. "cfFilter_<definitionId>")
 */ export function toCustomFieldFilterField(definitionId) {
    return `${CUSTOM_FIELD_FILTER_PREFIX}${definitionId}`;
}
/**
 * Check if a filter field represents a custom field filter
 * @param filterField The filter field string to check
 * @returns True if the filter field has the custom field filter prefix
 */ export function isCustomFieldFilter(filterField) {
    return filterField.startsWith(CUSTOM_FIELD_FILTER_PREFIX);
}
/**
 * Resolve the custom field definition ID from a custom field filter field.
 * Strips the prefix and returns the raw definition ID, or null if the field
 * is not a custom field filter.
 * @param filterField The filter field string to check
 * @returns The custom field definition ID if the field is a custom field filter, or null otherwise
 */ export function resolveCustomFieldDefinitionIdFromFilterField(filterField) {
    return isCustomFieldFilter(filterField) ? filterField.slice(CUSTOM_FIELD_FILTER_PREFIX.length) : null;
}
/**
 * Convert a custom field definition ID to a grouping option
 * @param definitionId The custom field definition ID
 * @returns The prefixed grouping option (e.g. "cfGroup_<definitionId>")
 */ export function toCustomFieldGroupingOption(definitionId) {
    return `${CUSTOM_FIELD_GROUPING_OPTION_PREFIX}${definitionId}`;
}
/**
 * Check if a grouping option represents a custom field grouping
 * @param groupingOption The grouping option string to check
 * @returns True if the grouping option has the custom field grouping prefix
 */ export function isCustomFieldGrouping(groupingOption) {
    return groupingOption.startsWith(CUSTOM_FIELD_GROUPING_OPTION_PREFIX);
}
/**
 * Resolve the custom field definition ID from a custom field grouping option.
 * Strips the prefix and returns the raw definition ID, or null if the option
 * is not a custom field grouping.
 * @param groupingOption The grouping option string to check
 * @returns The custom field definition ID if the option is a custom field grouping, or null otherwise
 */ export function resolveCustomFieldDefinitionIdFromGroupingOption(groupingOption) {
    return isCustomFieldGrouping(groupingOption) ? groupingOption.slice(CUSTOM_FIELD_GROUPING_OPTION_PREFIX.length) : null;
}
/**
 * Extract the custom field definition ID from a custom field column ID
 * @param columnId The prefixed custom field column ID
 * @returns The raw custom field definition ID with the prefix stripped
 */ export function getCustomFieldDefinitionId(columnId) {
    return columnId.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
}
/**
 * Returns the choice IDs of a SingleChoiceText custom field definition in display order.
 * Choices are ordered by their orderHint — matching how the choice editor presents them —
 * so sorting a choice column lines up with the field's defined option order.
 * @param definition The custom field definition
 * @returns Choice IDs ordered by orderHint
 */ export function getOrderedChoiceIds(definition) {
    return getSortedCustomFieldChoices(definition.choiceOptions).map((choice)=>choice.id);
}
/**
 * Resolves the ghost/placeholder text shown for an empty custom field of the given value type.
 * @param valueType The custom field value type
 * @returns The placeholder text for the given value type
 */ export function getCustomFieldPlaceholder(valueType) {
    switch(valueType){
        case CustomFieldValueType.Number:
            return Strings.CustomField_NumberPlaceholder;
        case CustomFieldValueType.Date:
            // The locale's date format pattern (e.g. "MM/DD/YYYY") itself — no extra wording needed.
            return getLocaleDateFormatPattern();
        case CustomFieldValueType.Flag:
            return Strings.CustomField_FlagPlaceholder;
        case CustomFieldValueType.SingleChoiceText:
            return Strings.CustomField_ChoicePlaceholder;
        case CustomFieldValueType.Text:
        default:
            return Strings.CustomField_TextPlaceholder;
    }
}
